import { useState, useEffect } from 'react';
import { api } from '../../../utils/api';
import { syncService } from '../../../services/syncService';

/**
 * Hook for loading and polling driver data, state, segments, and weekly distance.
 */
export function useDriverData(user, selectedVehicle, setSelectedVehicle) {
  const [driverInfo, setDriverInfo] = useState(null);
  const [driverState, setDriverState] = useState(null);
  const [currentSegment, setCurrentSegment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [weeklyDistance, setWeeklyDistance] = useState(null);
  const [syncStatus, setSyncStatus] = useState({ status: 'synced', isOnline: true });

  const loadDriverData = async () => {
    try {
      console.log('[DriverApp] Loading driver data for user:', user?.user_id);
      const driversRes = await api('/drivers');
      console.log('[DriverApp] Drivers response:', driversRes);

      const myDriver = driversRes.drivers?.find(d => d.user_id === user.user_id);
      console.log('[DriverApp] Found my driver:', myDriver?.driver_id);

      if (myDriver) {
        setDriverInfo(myDriver);

        // Restore preferred vehicle from backend if not already set locally
        if (!selectedVehicle && myDriver.preferred_vehicle_id) {
          try {
            const vRes = await api('/vehicles');
            const prefVehicle = (vRes.vehicles || []).find(v => v.vehicle_id === myDriver.preferred_vehicle_id);
            if (prefVehicle) {
              setSelectedVehicle(prefVehicle);
              localStorage.setItem('lawd_selected_vehicle', JSON.stringify(prefVehicle));
            }
          } catch (e) { /* non-critical */ }
        }

        const state = await api(`/drivers/${myDriver.driver_id}/state`);
        setDriverState(state);

        const segmentsRes = await api(`/drivers/${myDriver.driver_id}/segments?days=1`);
        const segments = segmentsRes.segments || [];

        // Load weekly distance in background (non-blocking)
        api(`/drivers/${myDriver.driver_id}/distance?days=7`)
          .then(dist => setWeeklyDistance(dist))
          .catch(() => setWeeklyDistance(null));

        const now = new Date();
        const ongoing = segments.find(s => {
          const endTs = new Date(s.end_ts);
          return endTs > now;
        });

        if (ongoing) {
          setCurrentSegment(ongoing);
          const startTs = new Date(ongoing.start_ts);
          const elapsed = Math.floor((now - startTs) / 1000);
          setElapsedTime(Math.max(0, elapsed));
        } else if (state?.current_segment) {
          // Segments endpoint missed the active segment (e.g. REST started > N days ago)
          // Use current_segment from the compliance state as fallback
          setCurrentSegment(state.current_segment);
          setElapsedTime(Math.max(0, state.current_segment.elapsed_seconds || 0));
        } else {
          setCurrentSegment(null);
          setElapsedTime(0);
        }
      } else {
        console.warn('[DriverApp] No driver found for user_id:', user?.user_id);
      }
    } catch (err) {
      console.error('[DriverApp] Failed to load driver data:', err);
    } finally {
      console.log('[DriverApp] Setting loading to false');
      setLoading(false);
    }
  };

  // Initial load + polling
  useEffect(() => {
    loadDriverData();
    const SYNC_INTERVAL = 45 * 60 * 1000;
    const interval = setInterval(loadDriverData, SYNC_INTERVAL);
    const unsubscribe = syncService.subscribe(setSyncStatus);
    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, []);

  // Elapsed time ticker
  useEffect(() => {
    const timer = setInterval(() => {
      if (currentSegment) {
        const startTs = new Date(currentSegment.start_ts);
        const elapsed = Math.floor((new Date() - startTs) / 1000);
        setElapsedTime(Math.max(0, elapsed));
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [currentSegment]);

  return {
    driverInfo, setDriverInfo,
    driverState, setDriverState,
    currentSegment, setCurrentSegment,
    loading,
    elapsedTime, setElapsedTime,
    weeklyDistance,
    syncStatus,
    loadDriverData,
  };
}
